import { useRef } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, ArrowUpRight, Check, BookOpen, Users, Share2, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useCMS, usePage, useStat, useSection } from "@/hooks/useCMS";
import { Eyebrow, StaggeredHeading, PillButton, MarqueeRibbon, JoinInBadge, ArrowImage, Reveal, TiltCard, QuoteRotator } from "@/components/motifs";
import { EventListingForm } from "@/components/EventListingForm";
import { CmsSection } from "@/components/CmsSection";
import { TemplateSections } from "@/components/TemplateSections";

const RINK = "https://images.pexels.com/photos/2289130/pexels-photo-2289130.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RACE = "https://images.pexels.com/photos/38675096/pexels-photo-38675096.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HIKE = "https://images.pexels.com/photos/5533872/pexels-photo-5533872.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

import { imgStyle } from "@/lib/imagePos";

const ICON_MAP = { Share2, CalendarDays, Users, BookOpen, Sparkles };

const PARTICIPATE = [
  { key: "post", tag: "Post It", accent: "#2CACD4", title: "Show us your moves", icon: Share2, tilt: "lg:-rotate-1", mt: "lg:mt-0",
    blurb: "Film your move, tag #ShowUsYourMoves, and join 50M+ interactions lighting up the country.", cta: "Share +", img: HIKE },
  { key: "host", tag: "Host It", accent: "#DB1A5C", title: "List a free event", icon: CalendarDays, tilt: "lg:rotate-2", mt: "lg:mt-8",
    blurb: "Organizers list any event for free and go live within days. No cost to run it, ever.", cta: "List +", img: RINK, to: "/Home#list" },
  { key: "join", tag: "Join It", accent: "#2CB6C2", title: "Move in-person or virtual", icon: Users, tilt: "lg:-rotate-3", mt: "lg:-mt-4",
    blurb: "From rink nights to sunrise yoga — find a move near you or stream one from home.", cta: "Find +", img: RACE, to: "/Events" },
];

function imgFrom(gallery, key, fallbackIdx) {
  const map = {
    winter_run: 0, seniors: 1, kids_gym: 2, virtual: 3, wheelchair: 4, cycling: 5, swim: 6,
  };
  const idx = map[key] ?? fallbackIdx ?? 0;
  return gallery[idx]?.url || gallery[0]?.url;
}

export default function Home() {
  const { data } = useCMS();
  const page = usePage("home");
  const gallery = data.gallery || [];
  const partners = data.partners || [];
  const quote = (data.quotes || [])[0] || {};
  const book = (data.books || [])[0] || {};
  const settings = data.settings || {};
  const galleryRef = useRef(null);
  const scrollGallery = (dir) => galleryRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  const secParticipate = useSection("participate");
  const secGallery = useSection("gallery");
  const secStories = useSection("stories");
  const secBenni = useSection("benni");
  const secCoalition = useSection("coalition");
  const secAdd = useSection("add_event");
  const stats = [
    { v: useStat("communities") || "500+", l: "communities" },
    { v: useStat("interactions") || "50M+", l: "interactions" },
    { v: useStat("partners") || "20+", l: "partners" },
  ];

  return (
    <div data-testid="home-page" className="flex flex-col">
      {/* HERO */}
      <section id="participate-top" className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 pt-14 pb-8 lg:pt-20">
          <div className="grid gap-12 lg:grid-cols-[7fr_5fr] lg:items-center">
            <div>
              <Eyebrow icon={Sparkles}>{page.eyebrow || "National Health & Fitness Foundation"}</Eyebrow>
              <div className="mt-5">
                <StaggeredHeading
                  animate
                  size="hero"
                  lines={[
                    { text: page.headline_1 || "One nation." },
                    { text: page.headline_2 || "In motion.", emphasis: true, accent: "#2CACD4" },
                    { text: page.headline_3 || "By 2030." },
                  ]}
                />
              </div>
              <p className="mt-7 max-w-xl text-lg text-muted-foreground">{page.intro}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <PillButton href="#participate" icon={ArrowRight} testid="hero-join">{page.cta_primary_label || "Join the Movement"}</PillButton>
                <PillButton href="#events" variant="ghost" testid="hero-find">{page.cta_secondary_label || "Find an Event"}</PillButton>
              </div>
              <div className="mt-12 flex gap-8 sm:gap-12">
                {stats.map((s) => (
                  <div key={s.l}>
                    <p className="font-display font-black italic text-3xl sm:text-4xl text-[#DB1A5C]">{s.v}</p>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-[#CAE295]/50 blur-2xl" aria-hidden="true" />
                <div className="absolute -bottom-8 -right-4 h-40 w-40 rounded-full bg-[#2CACD4]/30 blur-3xl" aria-hidden="true" />
                <JoinInBadge size={460} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <MarqueeRibbon words={settings.marquee_words} />
      </div>

      {/* THREE WAYS TO PARTICIPATE */}
      <CmsSection sk="participate">
      <section id="participate" className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24">
        <Reveal>
          <Eyebrow icon={Users}>{secParticipate.eyebrow || "Three ways to participate"}</Eyebrow>
          <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
            {secParticipate.heading || "Everyone moves."} <span className="italic text-[#2CACD4]">{secParticipate.subheading || "Your way."}</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {(data.participate?.length ? data.participate : PARTICIPATE).map((c, i) => {
            const Icon = typeof c.icon === "string" ? (ICON_MAP[c.icon] || Users) : (c.icon || Users);
            const img = c.image || c.img;
            const link = c.to || c.link;
            const tilt = c.tilt || ["lg:-rotate-1", "lg:rotate-2", "lg:-rotate-3"][i % 3];
            const mt = c.mt || ["lg:mt-0", "lg:mt-8", "lg:-mt-4"][i % 3];
            return (
            <Reveal key={c.key || c.id || i} delay={i * 0.1}>
              <TiltCard accent={c.accent} tilt={tilt} mt={mt} className="overflow-hidden h-full" testid={`participate-${c.key || i}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={img} alt={c.image_alt || c.title} className="h-full w-full object-cover" loading="lazy" style={imgStyle(c, "image")} />
                  <span className="absolute top-4 left-4 rounded-full px-3 py-1.5 text-xs font-black uppercase tracking-wider text-white shadow" style={{ background: c.accent }}>{c.tag}</span>
                </div>
                <div className="p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full" style={{ background: `${c.accent}22`, color: c.accent }}>
                    <Icon size={20} />
                  </span>
                  <h3 className="mt-4 font-display font-black uppercase text-xl">{c.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{c.blurb}</p>
                  {link ? (
                    <Link to={link} className="mt-4 inline-flex items-center gap-1 font-bold text-sm hover:text-[#2CACD4] transition-colors cursor-pointer">{c.cta}</Link>
                  ) : (
                    <a href="#gallery" className="mt-4 inline-flex items-center gap-1 font-bold text-sm hover:text-[#2CACD4] transition-colors cursor-pointer">{c.cta}</a>
                  )}
                </div>
              </TiltCard>
            </Reveal>
            );
          })}
        </div>
      </section>
      </CmsSection>

      {/* GALLERY */}
      <CmsSection sk="gallery">
      <section id="gallery" className="py-10 lg:py-16 bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <Eyebrow icon={Sparkles}>{secGallery.eyebrow || "Gallery"}</Eyebrow>
              <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl">{secGallery.heading || "Canada, in motion"}</h2>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <button onClick={() => scrollGallery(-1)} aria-label="Scroll gallery left" className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-background hover:bg-[#2CACD4] hover:text-slate-950 transition-colors cursor-pointer" data-testid="gallery-prev"><ChevronLeft size={20} /></button>
              <button onClick={() => scrollGallery(1)} aria-label="Scroll gallery right" className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-background hover:bg-[#2CACD4] hover:text-slate-950 transition-colors cursor-pointer" data-testid="gallery-next"><ChevronRight size={20} /></button>
            </div>
          </div>
        </div>
        <div
          ref={galleryRef}
          className="mt-8 flex gap-5 overflow-x-auto snap-x snap-mandatory no-scrollbar px-4 sm:px-8 pb-4 scroll-smooth"
          role="region"
          aria-label="Photo gallery of Canadians moving"
          tabIndex={0}
          data-testid="home-gallery"
        >
          {gallery.map((g, i) => (
            <figure key={i} className="snap-start shrink-0 w-72 sm:w-80">
              <img src={g.url} alt={g.alt} className="h-96 w-full rounded-3xl object-cover shadow-md" loading="lazy" />
            </figure>
          ))}
        </div>
      </section>
      </CmsSection>

      {/* FEATURED STORIES */}
      <CmsSection sk="stories">
      {settings.show_blog_home !== false && (data.articles || []).some((a) => a.show_on_home === "yes" || a.status === "featured") && (
        <section id="stories" className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24">
          <Reveal>
            <div className="flex items-end justify-between gap-4">
              <div>
                <Eyebrow icon={BookOpen}>{settings.blog_title || "Stories in Motion"}</Eyebrow>
                <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl">Latest stories</h2>
              </div>
              <PillButton to="/Blog" variant="ghost" testid="home-all-stories">All stories</PillButton>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {(data.articles || []).filter((a) => a.show_on_home === "yes" || a.status === "featured").slice(0, 3).map((a, i) => (
              <Reveal key={a.id || i} delay={i * 0.1}>
                <Link to={`/Blog/${a.slug}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`home-article-${i}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={a.cover} alt={a.cover_alt || a.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    {a.category && <span className="absolute top-4 left-4 rounded-full px-3 py-1.5 text-xs font-black uppercase text-white shadow" style={{ background: a.accent || "#2CACD4" }}>{a.category}</span>}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">{a.date}</p>
                    <h3 className="mt-2 font-display font-black uppercase text-xl leading-tight group-hover:text-[#2CACD4] transition-colors">{a.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3 flex-1">{a.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
      </CmsSection>

      {/* BENNI SPOTLIGHT */}
      <CmsSection sk="benni">
      <section id="benni" className="mx-auto max-w-7xl px-4 sm:px-8 py-16 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-6 dot-pattern -z-10 rounded-3xl" aria-hidden="true" />
              <div className="relative lg:-rotate-3">
                <img src={book.cover} alt={book.cover_alt || book.title || "Benni & Friends book cover"} className="w-full rounded-2xl shadow-2xl" loading="lazy" />
                <span className="absolute -top-4 -right-4 flex items-center gap-1.5 rounded-full bg-[#DB1A5C] px-4 py-2 text-xs font-black uppercase text-white shadow-lg">
                  <Sparkles size={14} /> {settings.launch_badge || "Relaunching Oct 5"}
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow icon={BookOpen}>Benni & Friends</Eyebrow>
            <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
              Meet Benni & Friends. <span className="italic text-[#2CACD4]">Stories that move.</span>
            </h2>
            <p className="mt-5 text-muted-foreground text-lg">Picture books that get small feet moving — read a chapter, then try the move together.</p>
            <div className="mt-7 flex gap-6">
              {[["3", "books"], ["4–8", "ages"], ["2×", "more likely to try"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-display font-black italic text-2xl text-[#DB1A5C]">{v}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <PillButton to="/BenniBooks" icon={ArrowRight} testid="home-benni-cta">Explore the series</PillButton>
            </div>
          </Reveal>
        </div>
      </section>
      </CmsSection>

      {/* COALITION */}
      <CmsSection sk="coalition">
      <section id="about" className="bg-muted/40 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <ArrowImage
              src={gallery[0]?.url}
              alt={gallery[0]?.alt || "Canadians moving together"}
              className="h-[420px] rounded-l-3xl"
              overlay={
                <div className="absolute bottom-6 left-6 rounded-2xl bg-white/95 backdrop-blur px-6 py-4 shadow-xl">
                  <p className="font-display font-black italic text-3xl text-[#DB1A5C]">500+</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Communities</p>
                </div>
              }
            />
          </Reveal>
          <Reveal delay={0.1}>
            <Eyebrow icon={Users}>{secCoalition.eyebrow || "The coalition"}</Eyebrow>
            <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-4xl leading-tight">{secCoalition.heading || "One goal, many moves."}</h2>
            <p className="mt-4 text-muted-foreground">Non-profits, for-profits and everyone in between, aligned behind two national moments and free listings for all.</p>
            <ul className="mt-6 space-y-3">
              {(secCoalition.bullets ? secCoalition.bullets.split("\n").filter(Boolean) : ["Winter Health & Fitness Week — every February", "National Health & Fitness Day — first Saturday of June", "Free national event listings for every organizer"]).map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#CAE295] text-slate-900"><Check size={14} strokeWidth={3} /></span>
                  <span className="text-foreground/80">{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-2">
              {partners.slice(0, 6).map((p) => (
                <span key={p.id || p.name} className="rounded-full bg-white border border-border px-4 py-2 text-sm font-semibold">{p.name}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
      </CmsSection>

      {/* PARTNER MARQUEE + QUOTE */}
      <CmsSection sk="partners_quote">
      <section id="events" className="bg-[#2CB6C2]/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-80">
            {partners.map((p) => (
              <span key={p.id || p.name} className="font-display font-black uppercase text-lg sm:text-xl text-foreground/50">{p.name}</span>
            ))}
          </div>
          {(data.quotes || []).length > 0 && (
            <Reveal className="mx-auto mt-14 max-w-3xl">
              <QuoteRotator quotes={data.quotes} />
            </Reveal>
          )}
        </div>
      </section>
      </CmsSection>

      {/* DARK ADD EVENT */}
      <CmsSection sk="add_event">
      <section id="list" className="bg-[#0F172A] text-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 grid gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow icon={CalendarDays} color="#CAE295">{secAdd.eyebrow || "Organizers"}</Eyebrow>
            <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
              {secAdd.heading || "Add your event."} <span className="italic text-[#2CACD4]">{secAdd.subheading || "It's free."}</span>
            </h2>
            <ul className="mt-7 space-y-3">
              {(secAdd.bullets ? secAdd.bullets.split("\n").filter(Boolean) : ["No cost to list or run — ever", "Live on the national directory within days", "Reach 500+ communities and 50M+ movers"]).map((t) => (
                <li key={t} className="flex items-start gap-3 text-white/80">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#CAE295] text-slate-900"><Check size={14} strokeWidth={3} /></span>
                  {t}
                </li>
              ))}
            </ul>
            <a href={`mailto:${settings.contact_email || "info@showusyourmoves.ca"}`} className="mt-8 inline-flex items-center gap-2 text-white/70 hover:text-white cursor-pointer">
              <ArrowUpRight size={18} /> {settings.contact_email || "info@showusyourmoves.ca"}
            </a>
          </div>
          <EventListingForm />
        </div>
      </section>
      </CmsSection>
      <TemplateSections page="home" />
    </div>
  );
}
